// components/settings/ContributorsPanel.jsx — Anamoria SPA
// v1.2 — NEW-26: status filter, defaulting to Active.
//   The list rendered every contributor regardless of status. In live pilot
//   data 25 of 31 rows were revoked (12 distinct emails - see NEW-13), so the
//   list read as long when the useful part of it was three rows. Filter chips
//   with counts; Active is the default view. No pagination and no search:
//   the longest real list is 18 rows, so both would be premature.
// v1.1 — Revoke UX + field name normalization (June 27, 2026)
//
// Changes from v1.0:
//   - "Remove" → "Revoke" throughout (button label, confirmation dialog).
//   - On revoke success: updates contributor status to 'revoked' in local
//     state with revokedAt timestamp (instead of filtering out). Revoked
//     contributors remain visible with muted styling and date.
//   - Field names aligned to backend v1.11 normalization:
//     contributor_name → contributorName, memory_count → memoryCount.
//   - Revoked rows: muted opacity, "Revoked" badge, revoked date, no
//     Revoke button. Active/pending rows show Revoke button as before.
//   - State renamed: contributorToRemove → contributorToRevoke.
//
// v1.0 — Contributors toggle + list + remove (April 11, 2026)
//
// Extracted from SpaceSettings.jsx v1.1 (Sections 1 toggle + 4)
// Independent save: PATCH /spaces/:id with { privacyMode }
// Remove: DELETE /spaces/:id/contributors/:contributorId
//
// Props:
//   space    — current space object (from SettingsPage state)
//   getApi   — stable API client factory
//   onSave   — callback with full updated space object (for parent state sync)

import { useState, useEffect, useRef, useCallback } from 'react';
import { getStatusKey, getStatusLabel, getMemoryLabel } from './settingsUtils';
import shared from './settingsShared.module.css';
import styles from './ContributorsPanel.module.css';

export default function ContributorsPanel({ space, getApi, onSave }) {
  // ─── Toggle state ───
  const [allowContributors, setAllowContributors] = useState(space.privacyMode !== 'private');

  // ─── Contributors list state ───
  const [contributors, setContributors] = useState([]);
  const [loadingContributors, setLoadingContributors] = useState(false);
  const [contributorToRevoke, setContributorToRevoke] = useState(null);
  // NEW-26: 'active' | 'pending' | 'revoked' | 'all'
  const [statusFilter, setStatusFilter] = useState('active');

  // ─── Save state (for toggle) ───
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  // ─── Timeout ref for cleanup ───
  const savedTimerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
    };
  }, []);

  // ─── Re-initialize toggle from space prop ───
  useEffect(() => {
    setAllowContributors(space.privacyMode !== 'private');
  }, [space.privacyMode]);

  // ─── Load contributors on mount ───
  useEffect(() => {
    let cancelled = false;

    async function loadContributors() {
      setLoadingContributors(true);
      try {
        const api = getApi();
        const data = await api.get(`/spaces/${space.id}/contributors`);
        if (!cancelled) {
          setContributors(data.contributors || []);
        }
      } catch (err) {
        console.error('ContributorsPanel: failed to load contributors:', err);
      } finally {
        if (!cancelled) setLoadingContributors(false);
      }
    }

    loadContributors();
    return () => { cancelled = true; };
  }, [space.id, getApi]);

  // ─── Save toggle (privacy mode) ───
  const handleSave = useCallback(async () => {
    setSaving(true);
    setError('');
    setSaved(false);

    try {
      const api = getApi();
      const body = {
        privacyMode: allowContributors ? 'shared' : 'private',
      };

      const updated = await api.patch(`/spaces/${space.id}`, body);

      if (onSave) onSave(updated);

      setSaved(true);
      savedTimerRef.current = setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      console.error('ContributorsPanel save error:', err);
      setError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  }, [allowContributors, getApi, space.id, onSave]);

  // ─── Revoke contributor ───
  const handleRevokeConfirm = useCallback(async () => {
    if (!contributorToRevoke) return;

    try {
      const api = getApi();
      const result = await api.delete(`/spaces/${space.id}/contributors/${contributorToRevoke.id}`);
      setContributors((prev) =>
        prev.map((c) =>
          c.id === contributorToRevoke.id
            ? { ...c, status: 'revoked', revokedAt: result.revokedAt || new Date().toISOString() }
            : c
        )
      );
      setContributorToRevoke(null);
    } catch (err) {
      console.error('ContributorsPanel: failed to revoke contributor:', err);
      setError('Failed to revoke contributor.');
    }
  }, [contributorToRevoke, getApi, space.id]);

  // ─── Stop propagation for confirm dialog ───
  function stopPropagation(e) {
    e.stopPropagation();
  }

  // ─── NEW-26: status counts + filtered view ───
  const statusCounts = contributors.reduce((acc, c) => {
    const key = getStatusKey(c.status);
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});
  const visibleContributors = statusFilter === 'all'
    ? contributors
    : contributors.filter((c) => getStatusKey(c.status) === statusFilter);
  const FILTERS = ['active', 'pending', 'revoked'];

  // ─── Status class lookup ───
  function statusClass(status) {
    const key = getStatusKey(status);
    if (key === 'active') return styles.statusActive;
    if (key === 'revoked') return styles.statusRevoked;
    return shared.badgePending;
  }

  // ─── Render ───
  return (
    <div>
      {/* ══════ Allow Contributors Toggle ══════ */}
      <div className={shared.section}>
        <h3 className={shared.sectionTitle}>CONTRIBUTORS</h3>

        <div
          className={shared.toggleRow}
          onClick={() => setAllowContributors(!allowContributors)}
          role="switch"
          aria-checked={allowContributors}
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setAllowContributors(!allowContributors);
            }
          }}
        >
          <div className={shared.toggleRowLeft}>
            <span className={shared.toggleLabel}>Allow Contributors</span>
            <span className={shared.toggleDesc}>Let family and friends contribute memories.</span>
          </div>
          <div className={`${shared.toggleTrack} ${allowContributors ? shared.toggleOn : ''}`}>
            <div className={shared.toggleKnob} />
          </div>
        </div>
      </div>

      {/* ══════ Contributor List (shown when toggle ON) ══════ */}
      {allowContributors && (
        <div className={shared.section}>
          <h3 className={shared.sectionTitle}>
            CONTRIBUTOR LIST ({visibleContributors.length})
          </h3>

          {/* NEW-26: status filter — Active is the default view. */}
          {!loadingContributors && contributors.length > 0 && (
            <div className={styles.filterRow} role="group" aria-label="Filter contributors by status">
              {FILTERS.map((key) => (
                <button
                  key={key}
                  type="button"
                  className={`${styles.filterChip} ${statusFilter === key ? styles.filterChipOn : ''}`}
                  aria-pressed={statusFilter === key}
                  onClick={() => setStatusFilter(key)}
                >
                  {getStatusLabel(key)} ({statusCounts[key] || 0})
                </button>
              ))}
              <button
                type="button"
                className={`${styles.filterChip} ${statusFilter === 'all' ? styles.filterChipOn : ''}`}
                aria-pressed={statusFilter === 'all'}
                onClick={() => setStatusFilter('all')}
              >
                All ({contributors.length})
              </button>
            </div>
          )}

          {loadingContributors && (
            <p className={shared.loadingText}>Loading...</p>
          )}

          {!loadingContributors && visibleContributors.length > 0 && (
            <ul className={styles.contributorList}>
              {visibleContributors.map((c) => {
                const isRevoked = c.status === 'revoked';
                return (
                  <li key={c.id} className={`${styles.contributorItem} ${isRevoked ? styles.contributorRevoked : ''}`}>
                    <div className={styles.contributorInfo}>
                      <span className={styles.contributorName}>{c.contributorName}</span>
                      <span className={statusClass(c.status)}>{getStatusLabel(c.status)}</span>
                    </div>
                    <div className={styles.contributorDetails}>
                      <span className={styles.contributorEmail}>{c.email}</span>
                      <span className={styles.contributorMemories}>{getMemoryLabel(c.memoryCount)}</span>
                      {isRevoked && c.revokedAt && (
                        <span className={styles.revokedDate}>
                          {new Date(c.revokedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </span>
                      )}
                    </div>
                    {!isRevoked && (
                      <button
                        className={styles.revokeBtn}
                        onClick={() => setContributorToRevoke(c)}
                      >
                        Revoke
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}

          {!loadingContributors && contributors.length === 0 && (
            <p className={styles.noContributors}>
              No contributors yet. Use the Invite tab to share your space.
            </p>
          )}

          {!loadingContributors && contributors.length > 0 && visibleContributors.length === 0 && (
            <p className={styles.noContributors}>
              No one here right now.
            </p>
          )}
        </div>
      )}

      {/* ══════ Footer: Save + feedback ══════ */}
      <div className={shared.panelFooter}>
        <button
          className={shared.saveBtn}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save'}
        </button>
        {saved && <span className={shared.savedFade}>Saved ✓</span>}
      </div>

      {error && <p className={shared.errorMsg}>{error}</p>}

      {/* ══════ Revoke Contributor Confirmation ══════ */}
      {contributorToRevoke && (
        <div className={styles.confirmOverlay} onClick={() => setContributorToRevoke(null)}>
          <div className={styles.confirmModal} onClick={stopPropagation}>
            <h3 className={styles.confirmTitle}>Revoke Contributor?</h3>
            <p className={styles.confirmMessage}>
              Revoke access for <strong>{contributorToRevoke.contributorName}</strong>?
              Their memories will remain, but they won't be able to add new ones.
            </p>
            <div className={styles.confirmActions}>
              <button
                className={styles.confirmCancelBtn}
                onClick={() => setContributorToRevoke(null)}
              >
                Cancel
              </button>
              <button
                className={styles.confirmRevokeBtn}
                onClick={handleRevokeConfirm}
              >
                Revoke
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
